import * as pkijs from 'pkijs';
import * as asn1js from 'asn1js';
import * as pvutils from 'pvutils';
import { type Organisation } from '../models/organisation';

const hashAlg = 'SHA-512';
const signAlg = 'RSA-PSS';
const keylength = 4096;

/***
 * @example
 * createPKCS10({ countryName: 'D', stateOrProvince: 'Denmark', localityName: Copenhagen, organisationName: 'Inpay' })
 *  .then(({csr, privateKey} => {...}))
 ***/
async function createPKCS10({
  countryName,
  stateOrProvince,
  localityName,
  organisationName
}: {
  countryName: string;
  stateOrProvince: string;
  localityName: string;
  organisationName: string;
}) {
  const crypto = pkijs.getCrypto(true);
  const keyPair = await generateKeyPair(crypto, getAlgorithm());

  return {
    csr: `-----BEGIN CERTIFICATE REQUEST-----\n${formatPEM(
      pvutils.toBase64(
        pvutils.arrayBufferToString(
          await createCSR(keyPair, {
            countryName,
            stateOrProvince,
            localityName,
            organisationName
          })
        )
      )
    )}\n-----END CERTIFICATE REQUEST-----`,
    privateKey: `-----BEGIN PRIVATE KEY-----\n${formatPEM(
      pvutils.toBase64(
        pvutils.arrayBufferToString(
          await crypto.exportKey('pkcs8', keyPair.privateKey)
        )
      )
    )}\n-----END PRIVATE KEY-----`
  };
}

async function createCSR(
  keyPair: CryptoKeyPair,
  {
    countryName,
    stateOrProvince,
    localityName,
    organisationName
  }: {
    countryName: string;
    stateOrProvince: string;
    localityName: string;
    organisationName: string;
  }
) {
  const pkcs10 = new pkijs.CertificationRequest();
  pkcs10.version = 0;
  //list of OID reference: http://oidref.com/2.5.4
  pkcs10.subject.typesAndValues.push(
    new pkijs.AttributeTypeAndValue({
      type: '2.5.4.6', //countryName
      value: new asn1js.PrintableString({ value: countryName })
    })
  );
  pkcs10.subject.typesAndValues.push(
    new pkijs.AttributeTypeAndValue({
      type: '2.5.4.8', //stateOrProvinceName
      value: new asn1js.Utf8String({ value: stateOrProvince })
    })
  );
  pkcs10.subject.typesAndValues.push(
    new pkijs.AttributeTypeAndValue({
      type: '2.5.4.7', //localityName
      value: new asn1js.Utf8String({ value: localityName })
    })
  );
  pkcs10.subject.typesAndValues.push(
    new pkijs.AttributeTypeAndValue({
      type: '2.5.4.10', //organizationName
      value: new asn1js.Utf8String({ value: organisationName })
    })
  );

  //add attributes to make CSR valid
  //Attributes must be "a0:00" if empty
  pkcs10.attributes = [];

  await pkcs10.subjectPublicKeyInfo.importKey(keyPair.publicKey);
  //signing final PKCS#10 request
  await pkcs10.sign(keyPair.privateKey, hashAlg);

  return pkcs10.toSchema().toBER(false);
}

// add line break every 64th character
function formatPEM(pemString: string) {
  return pemString.replace(/(.{64})/g, '$1\n');
}

function getAlgorithm() {
  const algorithm = pkijs.getAlgorithmParameters(signAlg, 'generateKey');

  if ('hash' in algorithm.algorithm)
    // @ts-expect-error modulusLength is actually present, TS for some reason is not aware of it
    algorithm.algorithm.modulusLength = keylength;
  // @ts-expect-error hash is also present, so this is a false positive
  algorithm.algorithm.hash.name = hashAlg;
  return algorithm;
}

function generateKeyPair(crypto: pkijs.ICryptoEngine, algorithm: any) {
  return crypto.generateKey(algorithm.algorithm, true, algorithm.usages);
}

export async function generateCertificates({
  orgDetail
}: {
  orgDetail: Organisation;
}) {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

  return createPKCS10({
    countryName: orgDetail.country_code ?? '',
    stateOrProvince: regionNames.of(orgDetail?.country_code ?? '') ?? '',
    localityName: orgDetail.city ?? '',
    organisationName: orgDetail.name
  }).then((pairKey) => {
    return pairKey;
  });
}
